import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PawPrint, HeartPulse, Baby, Milk, Egg, Bird, ClipboardList, PackageX, Receipt,
  IndianRupee, CheckCircle2, Plus, QrCode, Wallet, MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';
import { ILMSKPI } from '../components/ILMSKPI';
import { ILMSCard } from '../components/ILMSCard';
import { ILMSCardSkeleton } from '../components/ILMSLoadingState';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSButton } from '../components/ILMSButton';
import { ILMSPageHeader } from '../components/ILMSPageHeader';
import { ILMSDatePicker } from '../components/ILMSDatePicker';
import { formatCurrency, formatDate, formatNumber, today } from '../utils/format';
import { SmartChart } from '../features/charts/SmartChart';
import { useAuth } from '../hooks/useAuth';
import type { DashboardKPIs } from '../types';

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// Drops the run of zero-production days BEFORE the first real entry (on
// request) - a 30-day default range very often starts well before the farm
// began recording here, which otherwise draws a long flat "nothing to see"
// stretch ahead of the actual data. A zero day AFTER real data has started
// (a genuinely missed entry) stays visible - only the leading gap is cut.
function trimLeadingEmptyDays<T extends { total_litres: number }>(days: T[]): T[] {
  const firstIndex = days.findIndex((d) => d.total_litres > 0);
  return firstIndex <= 0 ? days : days.slice(firstIndex);
}

const RANGE_PRESETS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

function MilkProductionSection() {
  const [startDate, setStartDate] = useState(daysAgo(29));
  const [endDate, setEndDate] = useState(today());
  const chartQuery = useQuery({
    queryKey: ['dashboard-milk-chart', startDate, endDate],
    queryFn: () => dashboardApi.milkChart(startDate, endDate),
  });
  const data = chartQuery.data;
  const chartDays = data ? trimLeadingEmptyDays(data.days) : [];

  return (
    <ILMSCard title="Milk Production" subtitle="Litres collected by shift, Rate trend and total value — with Morning and Evening Fat % charted separately below.">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <ILMSDatePicker label="From" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
        <ILMSDatePicker label="To" value={endDate} max={today()} onChange={(e) => setEndDate(e.target.value)} />
        <div className="flex gap-1.5 pb-0.5">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => { setEndDate(today()); setStartDate(daysAgo(p.days - 1)); }}
              className="rounded-md border border-ink-300/30 px-2.5 h-9 text-helper-text text-ink-600 hover:border-brand-green-400 hover:text-brand-green-700"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {chartQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(chartQuery.error)}</ILMSAlert>}

      {chartQuery.isLoading && <ILMSCardSkeleton count={1} />}

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <ILMSKPI label="Total Litres" value={`${formatNumber(data.total_litres, 1)} L`} icon={<Milk />} tone="success" />
            <ILMSKPI label="Total Amount (₹)" value={formatCurrency(data.total_amount)} icon={<IndianRupee />} tone="info" />
            <ILMSKPI label="Morning Fat %" value={data.morning_fat_percent != null ? formatNumber(data.morning_fat_percent, 2) : '—'} helperText="Litres-weighted, this range" icon={<Milk />} tone="neutral" />
            <ILMSKPI label="Evening Fat %" value={data.evening_fat_percent != null ? formatNumber(data.evening_fat_percent, 2) : '—'} helperText="Litres-weighted, this range" icon={<Milk />} tone="neutral" />
          </div>
          {data.days.every((d) => d.total_litres === 0) ? (
            <p className="text-secondary text-ink-400 py-6 text-center">No milk sessions recorded in this range.</p>
          ) : (
            <SmartChart id="dashboard-milk" data={chartDays} />
          )}
          {data.days.some((d) => d.morning_fat_percent != null || d.evening_fat_percent != null) && (
            <div className="mt-4">
              <SmartChart id="dashboard-fat" data={chartDays} />
            </div>
          )}
        </>
      )}
    </ILMSCard>
  );
}

const QUICK_ACTIONS = [
  { label: 'Add Animal', to: '/animals/new', icon: Plus },
  { label: 'Scan QR', to: '/qr-scan', icon: QrCode },
  { label: 'Dairy', to: '/dairy', icon: Milk },
  { label: 'Health', to: '/health', icon: HeartPulse },
  { label: 'Breeding', to: '/breeding', icon: Baby },
  { label: 'Poultry', to: '/poultry', icon: Bird },
  { label: 'Tasks', to: '/tasks', icon: ClipboardList },
  { label: 'Expenses', to: '/procurement-finance', icon: Receipt },
];

// The herd split so each animal counts once (pregnant & milking is its own
// card, not inside both "pregnant" and "milking"); empty kinds are left out
// except the four the farm always has.
function herdParts(k: DashboardKPIs): { label: string; value: number; note: string; icon: React.ReactNode; tone: 'info' | 'success' | 'neutral' | 'warning' }[] {
  const parts = [
    { label: 'Milking', value: k.milking_only ?? 0, note: 'not pregnant', icon: <Milk />, tone: 'success' as const, keep: true },
    { label: 'Pregnant & Milking', value: k.pregnant_lactating ?? 0, note: 'milking now', icon: <Baby />, tone: 'info' as const, keep: true },
    { label: 'Pregnant & Dry', value: k.pregnant_dry ?? 0, note: 'not milking', icon: <Baby />, tone: 'info' as const, keep: true },
    { label: 'Dry', value: k.dry_only ?? 0, note: 'not pregnant', icon: <Milk />, tone: 'neutral' as const, keep: true },
    { label: 'Pregnant', value: k.pregnant_only ?? 0, note: 'milking / dry not set', icon: <Baby />, tone: 'info' as const, keep: false },
    { label: 'Other', value: k.other_status ?? 0, note: 'sick or not set', icon: <HeartPulse />, tone: 'warning' as const, keep: false },
  ];
  return parts.filter((p) => p.keep || p.value > 0);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const kpisQuery = useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () => dashboardApi.kpis(),
  });

  const kpis = kpisQuery.data;

  return (
    <div className="flex flex-col gap-5">
      <ILMSPageHeader
        title="Dashboard"
        subtitle={`Welcome back${user?.username ? `, ${user.username}` : ''}. Here is today's farm summary.`}
      />

      {kpisQuery.isError && (
        <ILMSAlert tone="critical" title="Unable to load dashboard data">
          {getApiErrorMessage(kpisQuery.error)}
          <div className="mt-2">
            <ILMSButton size="sm" variant="outline" onClick={() => kpisQuery.refetch()}>
              Retry
            </ILMSButton>
          </div>
        </ILMSAlert>
      )}

      {/* Quick actions */}
      <ILMSCard title="Quick actions" subtitle="Jump straight into the most common daily tasks.">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
          {QUICK_ACTIONS.map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.to)}
              className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-ink-300/20 py-3.5 px-2 text-center hover:border-brand-green-400 hover:bg-brand-green-50/40 transition-colors min-h-touch"
            >
              <action.icon className="h-5 w-5 text-brand-green-700" />
              <span className="text-secondary text-ink-700 leading-tight">{action.label}</span>
            </button>
          ))}
        </div>
      </ILMSCard>

      {kpisQuery.isLoading ? (
        <ILMSCardSkeleton count={6} />
      ) : (
        <>
          {/* Herd: totals, then every animal on the farm in exactly one status
              card - all on one line on a wide screen, so they simply add up
              (the sum is written out under them). */}
          <div>
            <p className="text-label text-ink-500 uppercase tracking-wide mb-2">Herd</p>
            {/* 9 cards: two rows of 5 + 4 on a normal screen, one line only when very wide. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 min-[1900px]:flex gap-3 sm:gap-4 min-[1900px]:[&>*]:flex-1 min-[1900px]:[&>*]:min-w-0">
              {/* Calves aren't in the animal counts until registered as their own Animal - shown here alongside. */}
              <ILMSKPI label="Total Animals" value={formatNumber(kpis?.total_animals)} icon={<PawPrint />} tone="info" onClick={() => navigate('/animals')}
                helperText={kpis?.departed_animals ? `${kpis.on_farm_animals} on farm · ${kpis.departed_animals} left` : undefined} />
              <ILMSKPI label="Active Animals" value={formatNumber(kpis?.active_animals)} icon={<CheckCircle2 />} tone="success" onClick={() => navigate('/animals?health_status=Active')} />
              <ILMSKPI
                label="Sick Animals"
                value={formatNumber(kpis?.sick_animals)}
                icon={<HeartPulse />}
                tone={kpis && kpis.sick_animals > 0 ? 'critical' : 'success'}
                helperText={kpis && kpis.sick_animals > 0 ? 'Needs attention' : 'All clear'}
                onClick={() => navigate('/health')}
              />
              {kpis && herdParts(kpis).map((p) => (
                <ILMSKPI key={p.label} label={p.label} value={formatNumber(p.value)} icon={p.icon} tone={p.tone} helperText={p.note} onClick={() => navigate('/animals')} />
              ))}
              {/* Calves on the farm not yet registered as their own Animal (Dairy -> Calf Records). */}
              {kpis && (
                <ILMSKPI label="Calves" value={formatNumber((kpis.calves_female ?? 0) + (kpis.calves_male ?? 0))} icon={<Baby />} tone="success"
                  helperText={`Female: ${kpis.calves_female ?? 0} | Male: ${kpis.calves_male ?? 0}`} onClick={() => navigate('/dairy?tab=calf-records')} />
              )}
            </div>
            {kpis && (
              <p className="mt-2 text-secondary text-ink-600">
                {herdParts(kpis).map((p) => p.value).join(' + ')} = <span className="font-semibold text-ink-900">{kpis.on_farm_animals} on the farm</span>
                <span className="text-ink-400"> · in all: {kpis.lactating_animals} milking, {kpis.pregnant_animals} pregnant, {kpis.dry_animals} dry</span>
                <span className="text-ink-400"> · plus {(kpis.calves_female ?? 0) + (kpis.calves_male ?? 0)} calves (Female: {kpis.calves_female ?? 0} | Male: {kpis.calves_male ?? 0})</span>
              </p>
            )}
          </div>

          {/* Today's production and finance side by side on a wide screen. */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
          <div>
            <p className="text-label text-ink-500 uppercase tracking-wide mb-2">Today's Production</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              <ILMSKPI label="Today's Milk" value={`${formatNumber(kpis?.todays_milk_litres, 1)} L`} icon={<Milk />} tone="success" onClick={() => navigate('/dairy')} />
              <ILMSKPI label="Today's Eggs" value={formatNumber(kpis?.todays_eggs)} icon={<Egg />} tone="success" onClick={() => navigate('/poultry')} />
              <ILMSKPI label="Active Poultry Batches" value={formatNumber(kpis?.active_poultry_batches)} icon={<Bird />} tone="info" onClick={() => navigate('/poultry')} />
            </div>
          </div>

          {/* Today's finance */}
          <div>
            <p className="text-label text-ink-500 uppercase tracking-wide mb-2">Today's Finance</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              <ILMSKPI label="Today's Expenses" value={formatCurrency(kpis?.todays_expenses)} icon={<Receipt />} tone="neutral" onClick={() => navigate('/procurement-finance')} />
              <ILMSKPI label="Today's Sales" value={formatCurrency(kpis?.todays_sales)} icon={<IndianRupee />} tone="success" onClick={() => navigate('/procurement-finance')} />
              <ILMSKPI
                label="Pending Expense Approvals"
                value={formatNumber(kpis?.pending_expense_approvals)}
                icon={<Wallet />}
                tone={kpis && kpis.pending_expense_approvals > 0 ? 'warning' : 'success'}
                onClick={() => navigate('/procurement-finance')}
              />
            </div>
          </div>
          </div>

          {/* Operations */}
          <div>
            <p className="text-label text-ink-500 uppercase tracking-wide mb-2">Operations</p>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
              <ILMSKPI
                label="Pending Tasks"
                value={formatNumber(kpis?.pending_tasks)}
                icon={<ClipboardList />}
                tone={kpis && kpis.pending_tasks > 0 ? 'warning' : 'success'}
                onClick={() => navigate('/tasks')}
              />
              <ILMSKPI
                label="Pending Milk Approvals"
                value={formatNumber((kpis?.pending_milk_approvals ?? 0) + (kpis?.pending_milk_sale_approvals ?? 0))}
                icon={<Milk />}
                tone={kpis && kpis.pending_milk_approvals + kpis.pending_milk_sale_approvals > 0 ? 'warning' : 'success'}
                helperText="Sessions + sales not counted until approved"
                onClick={() => navigate('/dairy')}
              />
              <ILMSKPI
                label="Low Stock Items"
                value={formatNumber(kpis?.low_stock_items)}
                icon={<PackageX />}
                tone={kpis && kpis.low_stock_items > 0 ? 'critical' : 'success'}
                onClick={() => navigate('/inventory')}
              />
              <ILMSKPI
                label="New Enquiries"
                value={formatNumber(kpis?.new_enquiries)}
                icon={<MessageSquare />}
                tone={kpis && kpis.new_enquiries > 0 ? 'info' : 'success'}
                helperText={kpis && kpis.new_enquiries > 0 ? 'From the website' : undefined}
                onClick={() => navigate('/enquiries')}
              />
            </div>
          </div>

          <MilkProductionSection />
        </>
      )}
    </div>
  );
}
